import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const cardSelector = ".feature-card, .contact-card, .value-card, .team-card, .chairman-card, .vision-mission-card, .project-card, .blog-card, .category-card, .location-card";

export function useVisualEffects() {
  const location = useLocation();

  useEffect(() => {
    const cleanups = [];
    const frame = requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const observe = (elements, callback, options) => {
        if (reduceMotion || !("IntersectionObserver" in window)) {
          elements.forEach((element) => callback({ target: element, isIntersecting: true }));
          return;
        }
        const observer = new IntersectionObserver((entries) => entries.forEach(callback), options);
        elements.forEach((element) => observer.observe(element));
        cleanups.push(() => observer.disconnect());
      };

      const revealElements = [...document.querySelectorAll("main [data-reveal]")];
      observe(revealElements, (entry) => entry.target.classList.toggle("is-visible", entry.isIntersecting), { threshold: .18, rootMargin: "0px 0px -8% 0px" });

      const cards = [...document.querySelectorAll(`main ${cardSelector}`)];
      observe(cards, (entry) => {
        entry.target.classList.toggle("card-3d-reveal", entry.isIntersecting && !reduceMotion);
      }, { threshold: .3 });

      if (!reduceMotion) {
        cards.forEach((card, cardIndex) => {
          if (!card.querySelector(":scope > .card-bubbles")) {
            const wrap = document.createElement("div");
            wrap.className = "card-bubbles";
            wrap.setAttribute("aria-hidden", "true");
            for (let index = 0; index < 5; index += 1) {
              const bubble = document.createElement("span");
              bubble.className = "card-bubble";
              const size = 10 + ((cardIndex * 11 + index * 7) % 22);
              bubble.style.cssText = `width:${size}px;height:${size}px;left:${8 + ((cardIndex * 17 + index * 19) % 84)}%;animation-delay:-${index}s;animation-duration:${5 + (index % 3)}s`;
              wrap.appendChild(bubble);
            }
            card.prepend(wrap);
            cleanups.push(() => wrap.remove());
          }
        });
      }

      const images = [...document.querySelectorAll("main img")].filter((image) => !image.closest(".ff-msg-avatar, .logo"));
      images.forEach((image) => image.classList.add("ff-img-3d"));
      observe(images, (entry) => entry.target.classList.toggle("ff-img-in", entry.isIntersecting), { threshold: .2, rootMargin: "0px 0px -6% 0px" });

      if (!reduceMotion) {
        images.forEach((image) => {
          const enter = () => image.classList.add("ff-img-hover-ready");
          const move = (event) => {
            const rect = image.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            image.style.setProperty("--ff-ry", `${(((event.clientX - rect.left) / rect.width - .5) * 20).toFixed(2)}deg`);
            image.style.setProperty("--ff-rx", `${(-((event.clientY - rect.top) / rect.height - .5) * 20).toFixed(2)}deg`);
            image.style.setProperty("--ff-s", "1.05");
          };
          const leave = () => ["--ff-ry", "--ff-rx", "--ff-s"].forEach((property) => image.style.removeProperty(property));
          image.addEventListener("mouseenter", enter);
          image.addEventListener("mousemove", move);
          image.addEventListener("mouseleave", leave);
          cleanups.push(() => { image.removeEventListener("mouseenter", enter); image.removeEventListener("mousemove", move); image.removeEventListener("mouseleave", leave); });
        });
      }

      const blogCards = [...document.querySelectorAll("main .blog-card")];
      observe(blogCards, (entry) => entry.target.classList.toggle("is-in", entry.isIntersecting), { threshold: .2, rootMargin: "0px 0px -8% 0px" });
      blogCards.forEach((card, index) => { card.style.transitionDelay = `${index * .12}s`; });

      if (!reduceMotion) {
        [...document.querySelectorAll("main .value-card, main .team-card, main .blog-card")].forEach((card) => {
          const move = (event) => {
            const rect = card.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width - .5;
            const y = (event.clientY - rect.top) / rect.height - .5;
            card.style.setProperty("--mx", `${(x + .5) * 100}%`);
            card.style.setProperty("--my", `${(y + .5) * 100}%`);
            card.style.setProperty("--tx", `${(-y * 14).toFixed(2)}deg`);
            card.style.setProperty("--ty", `${(x * 14).toFixed(2)}deg`);
          };
          const leave = () => { card.style.removeProperty("--tx"); card.style.removeProperty("--ty"); };
          card.addEventListener("mousemove", move);
          card.addEventListener("mouseleave", leave);
          cleanups.push(() => { card.removeEventListener("mousemove", move); card.removeEventListener("mouseleave", leave); });
        });
      }

      const particleFields = [
        [".v-particles", "v-particle", 22], [".j-particles", "j-particle", 18],
        [".vs-particles", "vs-particle", 20], [".m-dots", "m-dot", 18],
      ];
      particleFields.forEach(([selector, className, count]) => {
        const field = document.querySelector(selector);
        if (!field || field.children.length) return;
        for (let index = 0; index < count; index += 1) {
          const particle = document.createElement("span");
          particle.className = className;
          const size = 2 + (index % 4);
          particle.style.cssText = `width:${size}px;height:${size}px;left:${(index * 37) % 100}%;top:${(index * 23) % 100}%;animation-duration:${8 + (index % 8)}s;animation-delay:-${index % 10}s`;
          field.appendChild(particle);
        }
        cleanups.push(() => field.replaceChildren());
      });
    });

    return () => {
      cancelAnimationFrame(frame);
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [location.pathname]);
}
